import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link as MuiLink,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DataObjectIcon from "@mui/icons-material/DataObject";
import TableViewIcon from "@mui/icons-material/TableView";
import { alpha } from "@mui/material/styles";
import PublicIcon from "@mui/icons-material/Public";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InsightsIcon from "@mui/icons-material/Insights";

import { getCrawlResults, getCrawlTasks } from "../../services/web-services";
import { getAllResultsCount } from "../../services/generic_services";
import KpiTile from "../results/KpiTile";
import FiltersPanel from "../results/FiltersPanel";

const csvEscape = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const toCsv = (rows, columns) => {
  const header = columns.map((c) => csvEscape(c.label)).join(",");
  const body = rows
    .map((r) => columns.map((c) => csvEscape(c.get(r))).join(","))
    .join("\n");
  return `﻿${header}\n${body}\n`;
};

const downloadBlob = (content, mime, filename) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const WEB_COLOR = "#4FB6BC";

const SORT_FIELDS = [
  { value: "created_at", label: "Date" },
  { value: "title", label: "Title" },
  { value: "site_name", label: "Source" },
];

const inDateRange = (dateLike, range) => {
  if (range === "all" || !dateLike) return true;
  const d = new Date(dateLike).getTime();
  if (Number.isNaN(d)) return true;
  const now = Date.now();
  const cutoffMs = { "24h": 86400e3, "7d": 7 * 86400e3, "30d": 30 * 86400e3 }[range];
  return cutoffMs ? now - d <= cutoffMs : true;
};

const docSource = (d) => d.site_name || d.url || "Web";
const docDate = (d) => d.created_at || d.date;
const docId = (d) => (d._id && d._id.$oid) || d._id || d.id || d.url;
const docBody = (d) => d.content || d.text || d.summary || d.body || "";

const DocCard = ({ doc }) => {
  // Web confidence is stored as a 0–100 percentage (probability * 100)
  const confidencePct = typeof doc.confidence === "number"
    ? Math.round(doc.confidence)
    : null;
  const created = docDate(doc);
  const snippet = docBody(doc).replace(/\s+/g, " ").trim().slice(0, 280);

  return (
    <Card
      sx={{
        p: 2,
        borderLeft: `3px solid ${WEB_COLOR}`,
        "&:hover": { boxShadow: `0 2px 12px ${alpha(WEB_COLOR, 0.15)}` },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, gap: 2 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
            {doc.url ? (
              <MuiLink
                href={doc.url}
                target="_blank"
                rel="noreferrer"
                underline="hover"
                color="text.primary"
                sx={{ "&:hover": { color: WEB_COLOR } }}
              >
                {doc.title || doc.url || "Untitled document"}
              </MuiLink>
            ) : (
              doc.title || "Untitled document"
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {docSource(doc)}
          </Typography>
          {created && (
            <Typography variant="caption" color="text.disabled">
              {new Date(created).toLocaleString()}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* Confidence percentage — hidden for now
          {confidencePct !== null && (
            <Chip
              size="small"
              label={`${confidencePct}%`}
              sx={{
                backgroundColor: confidencePct >= 50
                  ? alpha(WEB_COLOR, 0.18)
                  : alpha("#999", 0.15),
                color: confidencePct >= 50 ? WEB_COLOR : "text.secondary",
                fontWeight: 600,
              }}
            />
          )}
          */}
          {doc.url && (
            <Tooltip title="Open document">
              <IconButton
                size="small"
                component="a"
                href={doc.url}
                target="_blank"
                rel="noreferrer"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {snippet && (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
          {snippet}
          {docBody(doc).length > 280 ? "…" : ""}
        </Typography>
      )}
    </Card>
  );
};

const WebResults = () => {
  const [searchParams] = useSearchParams();
  const siteId = searchParams.get("site_id");

  const [results, setResults] = useState([]);
  const [docsCount, setDocsCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [sortBy, setSortBy] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [allSources, setAllSources] = useState([]);

  const [filters, setFilters] = useState({
    search: "",
    dateRange: "all",
    confidenceMin: 0,
    sources: {},
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        sort_by: sortBy,
        sort_direction: sortDirection,
        search: filters.search,
      };
      if (siteId) params.site_id = siteId;
      const [pageData, counts] = await Promise.allSettled([
        getCrawlResults(params),
        getAllResultsCount(),
      ]);
      if (pageData.status === "fulfilled") setResults(pageData.value || []);
      if (counts.status === "fulfilled") setDocsCount(counts.value?.documents || 0);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, sortDirection, filters.search, siteId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Build the complete source list from tracked web tasks (one-time)
  useEffect(() => {
    (async () => {
      try {
        const tasks = await getCrawlTasks();
        const sources = Array.from(
          new Set((tasks || []).map((t) => t.site_name || t.url).filter(Boolean))
        ).sort();
        setAllSources(sources);
      } catch (e) {
        console.log("Failed to fetch web sources", e);
      }
    })();
  }, []);

  // Sources visible in the filter panel: prefer tracked tasks, fall back to what's in the page
  const sourceList = useMemo(() => {
    const fromResults = Array.from(new Set(results.map(docSource).filter(Boolean)));
    const merged = Array.from(new Set([...allSources, ...fromResults])).sort();
    return merged;
  }, [allSources, results]);

  const webSources = useMemo(
    () =>
      sourceList.map((src) => ({
        key: src,
        label: src,
        color: WEB_COLOR,
        checked: filters.sources[src] !== false,
      })),
    [sourceList, filters.sources]
  );

  const toggleSource = (key) => {
    setFilters((f) => ({
      ...f,
      sources: { ...f.sources, [key]: f.sources[key] === false },
    }));
    setPage(1);
  };

  const resetFilters = () =>
    setFilters({ search: "", dateRange: "all", confidenceMin: 0, sources: {} });

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return results.filter((doc) => {
      if (filters.sources[docSource(doc)] === false) return false;
      if (!inDateRange(docDate(doc), filters.dateRange)) return false;
      // confidence is 0–100; the slider's confidenceMin is 0–1
      const conf = typeof doc.confidence === "number" ? doc.confidence : 0;
      if (conf < filters.confidenceMin * 100) return false;
      if (q) {
        const hay = [doc.title, doc.url, docSource(doc), docBody(doc)]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [results, filters]);

  const kpis = useMemo(() => {
    const inWindow = (range) =>
      results.filter((d) => inDateRange(docDate(d), range)).length;
    const confidences = results
      .map((d) => d.confidence)
      .filter((c) => typeof c === "number");
    const avgConf = confidences.length
      ? confidences.reduce((a, b) => a + b, 0) / confidences.length
      : null;
    return {
      total: docsCount,
      last24h: inWindow("24h"),
      last7d: inWindow("7d"),
      avgConf,
    };
  }, [results, docsCount]);

  const [exportAnchor, setExportAnchor] = useState(null);

  const exportVisible = useCallback((format) => {
    const date = new Date().toISOString().slice(0, 10);
    const base = `web-results-${date}`;
    if (format === "json") {
      downloadBlob(JSON.stringify(filtered, null, 2), "application/json", `${base}.json`);
    } else {
      const cols = [
        { label: "source", get: (d) => docSource(d) },
        { label: "title", get: (d) => d.title || "" },
        { label: "url", get: (d) => d.url || "" },
        { label: "created_at", get: (d) => docDate(d) || "" },
        { label: "confidence", get: (d) => d.confidence ?? "" },
        { label: "prediction", get: (d) => d.prediction ?? "" },
      ];
      downloadBlob(toCsv(filtered, cols), "text/csv;charset=utf-8", `${base}.csv`);
    }
    setExportAnchor(null);
  }, [filtered]);

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1.5 }}>
        <MuiLink component={RouterLink} to="/results" underline="hover" color="text.secondary">
          All Results
        </MuiLink>
        <Typography variant="body2" color="text.primary">Web</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Web Results
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {docsCount.toLocaleString()} documents collected
            {siteId && (
              <Box component="span" sx={{ color: WEB_COLOR, ml: 1 }}>
                · filtered to one source
              </Box>
            )}
            {lastUpdated && (
              <Box component="span" sx={{ color: "text.disabled", ml: 1 }}>
                · updated {lastUpdated.toLocaleTimeString()}
              </Box>
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup value="results" exclusive size="small">
            <ToggleButton component={RouterLink} to="/tasks" value="tasks">
              Tasks
            </ToggleButton>
            <ToggleButton component={RouterLink} to="/web/results" value="results">
              Results
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            onClick={(e) => setExportAnchor(e.currentTarget)}
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon fontSize="small" />}
            endIcon={<ArrowDropDownIcon fontSize="small" />}
            disabled={filtered.length === 0}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
          >
            <MenuItem onClick={() => exportVisible("json")}>
              <ListItemIcon><DataObjectIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="JSON" secondary="Full structure" />
            </MenuItem>
            <MenuItem onClick={() => exportVisible("csv")}>
              <ListItemIcon><TableViewIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="CSV" secondary="Flat, Excel-ready" />
            </MenuItem>
          </Menu>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchAll} size="small" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* KPI strip */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4} md={4}>
          <KpiTile
            label="Total"
            value={kpis.total}
            unit="documents"
            accent={WEB_COLOR}
            icon={<PublicIcon fontSize="small" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <KpiTile
            label="Last 24h"
            value={kpis.last24h}
            unit="docs"
            accent={WEB_COLOR}
            loading={loading}
            caption="in current page"
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <KpiTile
            label="Last 7d"
            value={kpis.last7d}
            unit="docs"
            accent={WEB_COLOR}
            loading={loading}
            caption="in current page"
          />
        </Grid>
        {/* Avg confidence — hidden for now
        <Grid item xs={12} sm={4} md={4}>
          <KpiTile
            label="Avg confidence"
            value={kpis.avgConf !== null ? `${Math.round(kpis.avgConf)}%` : "—"}
            accent={WEB_COLOR}
            icon={<InsightsIcon fontSize="small" />}
            loading={loading}
            caption="in current page"
          />
        </Grid>
        */}
      </Grid>

      {/* Filters + documents */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <FiltersPanel
            sources={webSources}
            onSourceToggle={toggleSource}
            search={filters.search}
            onSearchChange={(v) => setFilters((f) => ({ ...f, search: v }))}
            dateRange={filters.dateRange}
            onDateRangeChange={(v) => setFilters((f) => ({ ...f, dateRange: v }))}
            confidenceMin={filters.confidenceMin}
            onConfidenceChange={(v) => setFilters((f) => ({ ...f, confidenceMin: v }))}
            onReset={resetFilters}
          />
        </Grid>

        <Grid item xs={12} md={9}>
          {/* Sort controls */}
          <Card sx={{ p: 1.5, mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  label="Sort by"
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                >
                  {SORT_FIELDS.map((f) => (
                    <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Direction</InputLabel>
                <Select
                  label="Direction"
                  value={sortDirection}
                  onChange={(e) => { setSortDirection(e.target.value); setPage(1); }}
                >
                  <MenuItem value="desc">Descending</MenuItem>
                  <MenuItem value="asc">Ascending</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.disabled" sx={{ ml: "auto" }}>
                Page {page}
              </Typography>
            </Stack>
          </Card>

          {results.length === 0 ? (
            <Card sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">
                No web documents yet. Start a crawl to see results here.
              </Typography>
            </Card>
          ) : filtered.length === 0 ? (
            <Card sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">
                No items match the current filters.
              </Typography>
            </Card>
          ) : (
            <Stack spacing={1.5}>
              {filtered.map((doc, idx) => (
                <DocCard key={docId(doc) || idx} doc={doc} />
              ))}
            </Stack>
          )}

          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Pagination
              count={10}
              page={page}
              color="primary"
              onChange={(_, p) => setPage(p)}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WebResults;
